import HomeSider from '@/components/Home/HomeSider.js';
import {Button} from '@/components/ui/button';
import {Card, CardContent} from '@/components/ui/card';
import homePaths from '@/constants/homePaths';
import {useKayAuth} from '@/context/AuthContext.tsx';
import backendApis from '@/services/backendApis';
import {AgentTaskState} from '@/types';
import CryptoJS from 'crypto-js';
import {ArrowLeft, FileIcon, InfoIcon, Upload, X} from 'lucide-react';
import React, {useEffect, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import {useNavigate} from 'react-router-dom';
import {toast} from 'sonner';
import {animals, colors, uniqueNamesGenerator} from 'unique-names-generator';
import {TooltipContent, TooltipTrigger, Tooltip} from "@/components/ui/tooltip.js";
import {CarrierConnectionStatus} from "@/components/Settings/Connections.js";
import constants from "@/constants/constants.js";
import {
  carrierSecretKey,
  countLength,
  formatTitle,
  getCarrierName,
  getLocalStorage
} from "@/helpers/helperFunctions.js";
import {Label, Skeleton} from "@/components/ui/index.js";
import linesOfBusiness from "@/constants/linesOfBusiness.js";
import {FieldJobType} from "@/types/workflowDocs.js";

const NewApplication: React.FC = () => {
  const navigate = useNavigate();
  const {organization, user, workflowId} = useKayAuth();
  const userId = user?.user_id;
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState<File[]>([]);
  const [workflow, setWorkflow] = useState<any>(null)

  const [connectionStatus, setConnectionStatus] = useState<Record<string, CarrierConnectionStatus>>({});
  const allCarrierAccess = user?.carrier_access || [];

  useEffect(() => {
    fetchConnectionStatus();
  }, [organization]);

  const isStoreLocal = (carrierName: string) => {
    const orgCarrier = allCarrierAccess.find(c => c.carrier_name === getCarrierName(carrierName));
    return orgCarrier?.credential_storage === constants.localFlag;
  }
  const checkLocalSecretExists = (carrierName: string) => {
    const carrierKey = carrierSecretKey(carrierName);
    const encryptedSecret = getLocalStorage(carrierKey);
    return !!encryptedSecret;
  };

  const fetchConnectionStatus = async () => {
    if (!organization) {
      return;
    }

    const statusPromises = allCarrierAccess.map(async (carrier) => {
      const carrierName = carrier.carrier_name;
      const storeLocal = isStoreLocal(carrierName);

      const params1 = {credentials_name: carrierName, user_id: userId};
      const params2 = {carrier_name: carrierName, user_id: userId};
      // const browserStateExists = await backendApis.browserStateExists(params2)
      const browserStateExists = {data: {result: true}};
      const result: any = {
        loading: false,
        name: carrierName,
      };
      result.browserStateExists = browserStateExists.data.result;
      if (storeLocal) {
        result.credentialsExists = checkLocalSecretExists(carrierName);
      } else {
        const response = await backendApis.secretExists(params1);
        result.credentialsExists = response?.data?.result;
      }
      return result;
    });
    const results = await Promise.all(statusPromises);
    const status: Record<string, CarrierConnectionStatus> = {};
    results.forEach((result) => {
      if (result) {
        status[result.name] = {
          browserStateExists: result.browserStateExists,
          connected: result.credentialsExists && result.browserStateExists,
          credentialsExists: result.credentialsExists,
          loading: false
        };
      }
    });
    setConnectionStatus(status);
  };

  const getWorkflow = async () => {
    try {
      const res = await backendApis.getWorkflowById({workflow_id: workflowId});
      if (res.err) {
        toast.error(`Error getting workflow: ${res.err}`);
        return;
      }
      const data = res.data.workflow;
      setWorkflow(data);
    } catch (e: any) {
      console.error('Error getting workflow:', e);
    }
  }

  useEffect(() => {
    if (workflowId) {
      getWorkflow();
    }
  }, [workflowId]);

  const [selectedCarriers, setSelectedCarriers] = useState<string[]>([]);
  const [lob, setLob] = useState<string | null>(null);

  const onDrop = (acceptedFiles: File[]) => {
    const maxFileSize = 20 * 1024 * 1024; // 20MB in bytes
    const validFiles: File[] = [];
    const oversizedFiles: string[] = [];

    acceptedFiles.forEach((file) => {
      if (file.size <= maxFileSize) {
        validFiles.push(file);
      } else {
        oversizedFiles.push(file.name);
      }
    });

    if (oversizedFiles.length > 0) {
      toast.error(`The following files exceed the 20MB limit and were not uploaded: ${oversizedFiles.join(', ')}`);
    }

    if (validFiles.length > 0) {
      setDocuments([...documents, ...validFiles]);
    }
  };

  const {getInputProps, getRootProps, isDragActive} = useDropzone({onDrop});

  const uploadDocuments = async () => {
    const uploadedIds = await Promise.all(
      documents.map(async (file) => {
        try {
          const formData = new FormData();
          formData.append('file', file);
          const params = {
            metadata: {
              file_type: file.type,
              original_filename: file.name,
            }
          };
          const res = await backendApis.getDocumentUrl(params);
          if (res.err) {
            toast.error(`Error getting document url for ${file.name}`);
            throw new Error(res.err);
          }
          const uploadUrl = res.data.upload_url;
          const documentId = res.data.document_id;
          await uploadFileToS3(uploadUrl, file);
          return documentId;
        } catch {
          toast.error(`Error uploading ${file.name}`);
          return null;
        }
      }),
    );
    return uploadedIds.filter((id) => id !== null);
  };

  const uploadFileToS3 = async (newFilename: string, file: File) => {
    try {
      const fileContents = await file.arrayBuffer();
      const binary = new Uint8Array(fileContents);
      const blob = new Blob([binary]);
      const res = await fetch(newFilename, {
        body: blob,
        method: 'PUT',
      });
      if (res.ok) {
        return true;
      } else {
        console.error('Error uploading file to S3');
        return false;
      }
    } catch (error) {
      console.error('Error uploading file to S3:', error);
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!documents.length) {
      toast.error('Please upload at least one document');
      return;
    }
    if (!selectedCarriers.length) {
      toast.error('Please select at least one carrier');
      return;
    }
    if (!lob) {
      toast.error('Please select line of business');
      return;
    }
    setLoading(true);
    try {
      const uploadedIds = await uploadDocuments();
      if (countLength(uploadedIds) < 1) {
        toast.error('Error uploading documents. Please try again');
        return;
      }
      const newName = uniqueNamesGenerator({
        dictionaries: [colors, animals],
        length: 2,
        separator: '-',
      });
      const browserRuns: any[] = [];
      for (const carrier of selectedCarriers) {
        const carrierName = (carrier as string)
          ?.toLowerCase()
          .replaceAll(/\s/g, '_');
        browserRuns.push({
          browser_run_state: AgentTaskState.PENDING,
          browser_state_name: carrierName,
          browser_workflow_id: `${lob}/${carrierName}`,
          business_line: lob,
          carrier_name: carrierName,
          user_id: userId,
        });
      }
      const params: any = {
        browser_runs: browserRuns,
        documents: [],
        workflow_context_id: null,
        workflow_id: workflowId,
        workflow_run_description: '',
        workflow_run_name: newName,
      };
      const response = await backendApis.createWorkflowRun(params);
      if (response.err) {
        toast.error('Failed to create Application');
        console.log(`Failed to create Application: ${response.err}`);
        return;
      }
      toast.success('Application created successfully');
      const task = response.data.workflow_run;
      const ingestParams = {
        document_ids: uploadedIds,
        workflow_run_id: task.workflow_run_id,
      };
      const res = await backendApis.ingestDocuments(ingestParams);
      if (res.err) {
        toast.error('Error ingesting documents');
        console.log(`Error ingesting documents: ${res.err}`);
        return;
      }
      console.log(`Documents ingested successfully: ${res.data}`);
      console.log('Uploading local secrets to the server');
      await uploadLocalSecretsToServer();
      console.log('Local secrets uploaded to the server successfully');
      const url = [homePaths.home, homePaths.hub].join('') + '/' + task.workflow_run_id;
      navigate(url);
    } catch (error) {
      toast.error('Error creating Application');
      console.log(`Error creating Application: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const removeDocument = (index: number) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const changeCarrier = (carrier: string) => {
    setSelectedCarriers(prev => {
      if (prev.includes(carrier)) {
        return prev.filter(a => a !== carrier);
      }
      return [...prev, carrier];
    })
  }

  const goBack = () => {
    navigate([homePaths.home].join(''), {replace: true});
  };

  const uploadLocalSecretsToServer = async () => {
    const carrierAccess = user?.carrier_access || [];
    const localStorageItem = carrierAccess?.find(
      (a) => a.credential_storage === constants.localFlag,
    );
    const storeLocal = !!localStorageItem;
    const encryptionKey =
      import.meta.env.VITE_SECRET_ENCRYPTION_KEY || constants.sampleKey;
    const decrypt = (ciphertext: string) =>
      CryptoJS.AES.decrypt(ciphertext, encryptionKey).toString(
        CryptoJS.enc.Utf8,
      );
    if (!storeLocal) {
      return;
    }
    try {
      const secrets = allCarrierAccess
        .map((carrier) => {
          const carrierName = carrier.carrier_name;
          const carrierKey = carrierSecretKey(carrierName);
          const encryptedSecret = getLocalStorage(carrierKey);
          if (!encryptedSecret) {
            return null;
          }
          const credentials = JSON.parse(decrypt(encryptedSecret));
          return {carrierName, credentials};
        })
        .filter(Boolean);

      for (const secret of secrets) {
        const params = {
          credentials: secret?.credentials,
          credentials_name: secret?.carrierName,
        }
        await backendApis.createSecret(params);
      }
      toast.success('Local secrets uploaded to the server successfully');
    } catch (error) {
      console.error('Error uploading local secrets:', error);
      toast.error('Error uploading local secrets');
    }
  };

  const getDocumentMessage = () => {
    if (workflow) {
      const jobs = workflow.jobs;
      const prereqJob = jobs.find(a => a.job_type === FieldJobType.PREREQUISITE_VALIDATION);
      let str = ``;
      if (prereqJob) {
        const documentClasses = prereqJob?.job_args?.prerequisites?.document_classes || [];
        documentClasses.map((a) => {
          const docClass = a.document_class;
          const description = a.description;
          if (docClass && description) {
            str += `${docClass}: ${description}\n`;
          }
        })
        return str;
      }
    }
  }

  const changeLob = (line: string) => {
    setSelectedCarriers([]);
    setLob(line);
  }

  const docinfo = getDocumentMessage();

  const insuranceLines = allCarrierAccess.flatMap(a => a.insurance_lines);
  const uniqueLines = [...new Set(insuranceLines)]

  useEffect(() => {
    if (uniqueLines && countLength(uniqueLines) > 0) {
      setLob(uniqueLines[0]);
    }
  }, []);

  const disabledButton = countLength(documents) === 0 || loading ||
    countLength(selectedCarriers) === 0 || !lob;

  return (
    <div className="flex h-screen">
      <HomeSider/>

      <main className="flex-1 space-y-3 overflow-scroll bg-kay-slate p-6">

        <div className="container mx-auto p-6 space-y-6">
          <div className='space-y-3'>
            <div>
              <Button
                className="text-gray-600 hover:text-gray-900"
                onClick={() => goBack()}
                variant="link"
              >
                <ArrowLeft className="mr-2 h-4 w-4"/>
                <span>
                  Back to Applications
                </span>
              </Button>
            </div>
            <div className="flex items-center">
              <h1 className="text-xl font-medium tracking-wide">New application</h1>
            </div>
          </div>

          <Card className='bg-white'>
            <CardContent>
              <div className="space-y-6 py-3">
                <div>
                  <div className='flex items-center gap-3'>
                    <div className="text-md font-medium py-3">
                      Upload Documents
                    </div>
                    {
                      docinfo && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <InfoIcon className='p-0.5 cursor-pointer' />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p
                              className='max-w-xl text-sm bg-gray-500 rounded-md text-white p-3'
                            >
                              {docinfo}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      )
                    }
                  </div>
                  <div
                    {...getRootProps()}
                    className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center ${
                      isDragActive
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300'
                    }`}
                  >
                    <input {...getInputProps()} />
                    <Upload className="mx-auto mb-2"/>
                    <p>
                      {isDragActive
                        ? 'Drop files here'
                        : 'Drag & drop files or click to select'}
                    </p>
                  </div>
                  <div className="grid grid-cols-4 gap-2 my-2">
                    {documents.map((doc, index) => (
                      <div
                        className="flex items-center justify-between rounded-lg border border-gray-600 bg-gray-100 p-2"
                        key={index}
                      >
                        <div className="flex items-center">
                          <FileIcon className="mr-2"/>
                          <span className="line-clamp-1 truncate text-sm">
                            {doc.name}
                          </span>
                        </div>
                        <Button
                          className='p-1 hover:bg-white'
                          onClick={() => removeDocument(index)}
                          variant="ghost"
                        >
                          <X size={18} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className='space-y-3'>
                  <Label>Select Preferences</Label>
                  <div className='space-y-1'>
                    <Label>Lines of Business</Label>
                    {
                      uniqueLines.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {uniqueLines.map((line) => {
                            const lineObj = linesOfBusiness.find(a => a.key === line);
                            return (
                              <Button
                                className="rounded-full"
                                key={line}
                                onClick={() => changeLob(line)}
                                variant={lob===line ? 'default' : 'outline'}
                              >
                                {lineObj?.name || formatTitle(line)}
                              </Button>
                            )
                          })}
                        </div>
                      ) : (
                        <div>
                          <Skeleton className='h-6 bg-gray-400 animate-pulse w-3/4'/>
                        </div>
                      )
                    }
                  </div>

                  <div className='space-y-1'>
                    <Label>Carriers</Label>
                    {
                      Object.keys(connectionStatus).length > 0 ? (
                        <div
                          className="flex flex-wrap gap-2"
                        >
                          {
                            Object.keys(connectionStatus).map((carrierName) => {
                              const v = connectionStatus[carrierName];
                              const carrierAccess = allCarrierAccess.find(a => a.carrier_name === carrierName);
                              const lobSupported = carrierAccess?.insurance_lines.includes(lob);
                              const key = carrierName.toLowerCase();
                              const connected = v.connected;
                              if (!lobSupported) {
                                return null;
                              }
                              if (!connected) {
                                return (
                                  <Tooltip key={carrierName}>
                                    <TooltipTrigger asChild>
                                      <Button
                                        className="opacity-50 rounded-full"
                                        onClick={() => {
                                          toast.warning(`Carrier isn't connected. Connect this carrier to enable this option.`);
                                        }}
                                        title={'Connect this carrier to enable this option.'}
                                        variant='outline'
                                      >
                                        {formatTitle(carrierName)}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p
                                        className="max-w-xs text-sm bg-gray-500 rounded-md text-white p-3"
                                      >
                                        {'Connect this carrier to enable this option.'}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                )
                              }
                              const isSelected = selectedCarriers.includes(key);
                              return (
                                <Button
                                  className="rounded-full"
                                  key={key}
                                  onClick={() => changeCarrier(carrierName)}
                                  variant={isSelected ? 'default' : 'outline'}
                                >
                                  {formatTitle(carrierName)}
                                </Button>
                              );
                            })
                          }
                        </div>
                      ) : (
                        <div>
                          <Skeleton className='h-6 bg-gray-400 animate-pulse w-3/4'/>
                        </div>
                      )
                    }
                  </div>
                </div>
                <Button
                  className="w-full"
                  disabled={disabledButton}
                  onClick={handleSubmit}
                  variant='kay'
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <span>Creating...</span>
                    </div>
                  ) : 'Create Application'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default NewApplication;
